// The Models panel (/models, spec-v3-formats.md): every model the studio makes things with, by kind (video, images,
// voices, music, sound effects), each with its price in credits, what it's good at, what it won't do, and whether
// it's the default. Users set their account default here. The Director opens it on a tab (show_models).
// Models are grouped by `step` on this side, so it works whether or not the api adds categories.
import { useState } from "react";
import { tapTool } from "../actions";
import { api } from "../api";
import { toggleAudio } from "../audio";
import { Empty, ErrorNote, Icon, useLoad } from "../components/bits";
import { useModels } from "../components/ModelPicker";
import { ShowcaseSection } from "../components/ShowcaseGrid";
import { ImageShowcaseSection, StillStrip, useImageShowcase } from "../components/ImageShowcase";
import type { ImageShowcaseData } from "../showcaseImages";
import { Shell } from "../components/Shell";
import { MODEL_TABS, type ModelTab } from "../directives";
import { director } from "../director";
import { priceLabel } from "../models";
import { Link, navigate, useLocation } from "../router";
import { toast, useStore } from "../store";
import type { ModelInfo, MyModels, VoiceInfo } from "../types";

const PROVIDER: Record<string, string> = { fal: "fal", runway: "Runway", google: "Google", gemini: "Google", elevenlabs: "ElevenLabs", kling: "Kling", seedance: "ByteDance", luma: "Luma", openai: "OpenAI" };
export const providerOf = (m: Pick<ModelInfo, "id">) => {
  const p = m.id.split(":")[0] ?? "";
  return PROVIDER[p] ?? p.replace(/^./, (c) => c.toUpperCase());
};

const TAB_LEDE: Record<ModelTab, string> = {
  video: "Turns an approved frame into a moving clip. Priced per 5 seconds of video.",
  images: "Paints each scene's frame and your characters' sheets. Priced per picture.",
  voices: "Reads the narration and every character's lines.",
  music: "Composes the score or the beat under your video.",
  sfx: "Places sounds on the moments in each clip.",
};

/** "25 credits per 5 s", "8 credits per image". */
function price(m: ModelInfo) {
  if (m.creditsPer5s != null) return { n: m.creditsPer5s, unit: "per 5 s" };
  if (m.credits != null) return { n: m.credits, unit: m.unit ? `per ${m.unit}` : "each" };
  return null;
}

export function ModelsScreen() {
  const { query } = useLocation();
  const asked = query.get("tab");
  const tab: ModelTab = (MODEL_TABS.find((t) => t.id === asked)?.id ?? "video") as ModelTab;
  const models = useModels();
  const { data: mine, setData: setMine, error } = useLoad<MyModels | null>(() => api.myModels().catch(() => null), []);
  const step = MODEL_TABS.find((t) => t.id === tab)!.step;
  const list = (models ?? []).filter((m) => m.step === step);
  const current = mine?.account[step] || mine?.defaults[step] || list.find((m) => m.isDefault)?.id;
  const [busy, setBusy] = useState<string | null>(null);
  // MS-IMG: every picture model's showcase stills, on the Images tab's grid and each image model's card
  const stills = useImageShowcase();

  const choose = async (m: ModelInfo) => {
    setBusy(m.id);
    try {
      await api.setMyModel(m.step, m.id);
      setMine({ defaults: mine?.defaults ?? {}, account: { ...mine?.account, [m.step]: m.id } });
      director.context(`The user made ${m.label} their default for ${MODEL_TABS.find((t) => t.step === m.step)?.label.toLowerCase()} on the Models panel.`);
      toast(`${m.label} is your default from now on.`, "good");
    } catch (e) {
      toast(`Couldn't change it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Shell>
      <div className="page models-page">
        <header className="page-head">
          <h1 className="page-title">Models</h1>
          <p className="page-lede">What your videos are made with, and what each one costs. Pick a default for your account; any project or scene can still use another. Or ask: “which video model is best for people?”</p>
        </header>

        <nav className="mtabs" aria-label="Kinds of model">
          {MODEL_TABS.map((t) => {
            const n = (models ?? []).filter((m) => m.step === t.step).length;
            return (
              <button key={t.id} type="button" className={`mtab${t.id === tab ? " is-on" : ""}`} aria-current={t.id === tab ? "page" : undefined} onClick={() => navigate(t.id === "video" ? "/models" : `/models?tab=${t.id}`, { replace: true })}>
                {t.label}
                {models && <span className="mtab-n">{n}</span>}
              </button>
            );
          })}
        </nav>

        <p className="muted models-lede">{TAB_LEDE[tab]}</p>
        {error && <ErrorNote text={`Couldn't load your defaults. ${error}`} />}
        {tab === "video" && <ShowcaseSection />}
        {tab === "images" && <ImageShowcaseSection />}

        {!models ? (
          <ul className="mcards" aria-label="Loading the models">
            {[0, 1, 2].map((i) => (
              <li key={i} className="mcard mcard-skel" />
            ))}
          </ul>
        ) : !list.length ? (
          <Empty title="Nothing here yet">No models of this kind are offered right now.</Empty>
        ) : (
          <ul className="mcards">
            {list.map((m) => (
              <li key={m.id}>
                <ModelCard m={m} mine={m.id === current} accountPick={mine?.account[step] === m.id} busy={busy === m.id} onChoose={() => void choose(m)} stills={step === "image" ? stills : null} />
              </li>
            ))}
          </ul>
        )}

        {tab === "voices" && <NarratorVoices />}
        <p className="muted models-foot">
          Prices are in credits. <Link to="/credits">See credit packs</Link>. A rate marked estimate hasn't been confirmed against the provider's price list yet.
        </p>
      </div>
    </Shell>
  );
}

function ModelCard({ m, mine, accountPick, busy, onChoose, stills = null }: { m: ModelInfo; mine: boolean; accountPick: boolean; busy: boolean; onChoose: () => void; stills?: ImageShowcaseData | null }) {
  const p = price(m);
  const facts = [m.durations?.length ? `${m.durations.join(" or ")} s clips` : null, m.aspects?.length ? m.aspects.join(", ") : null].filter(Boolean);
  return (
    <article className={`mcard${mine ? " is-mine" : ""}`} aria-label={m.label}>
      <header className="mcard-head">
        <span className="mcard-provider">{providerOf(m)}</span>
        <h2 className="mcard-name">{m.label}</h2>
        <span className="mcard-tags">
          {m.isDefault && <span className="tag">Studio default</span>}
          {accountPick && <span className="tag tag-accent">Your default</span>}
        </span>
      </header>
      <p className="mcard-price">
        {p ? (
          <>
            <b>{p.n}</b> credits <span>{p.unit}</span>
            {m.creditsWithRefs != null && m.creditsWithRefs !== m.credits && (
              <span className="mcard-refs">
                {" "}· <b>{m.creditsWithRefs}</b> with reference pictures
              </span>
            )}
          </>
        ) : (
          priceLabel(m)
        )}
        {m.rateSource === "unverified" && (
          <span className="mcard-est" title="This rate hasn't been confirmed against the provider's price list yet">
            Estimate
          </span>
        )}
      </p>
      {!!facts.length && <p className="mcard-facts">{facts.join(". ")}</p>}
      {stills && <StillStrip data={stills} modelId={m.id} label={m.label} price={false} />}
      {!!m.strengths.length && (
        <ul className="mcard-list" aria-label="Good at">
          {m.strengths.map((s) => (
            <li key={s}>
              <Icon name="check" size={14} /> {s.replace(/^./, (c) => c.toUpperCase())}
            </li>
          ))}
        </ul>
      )}
      {!!m.constraints.length && (
        <ul className="mcard-list mcard-limits" aria-label="Limits">
          {m.constraints.map((c) => (
            <li key={c}>
              <Icon name="alert" size={14} /> {c.replace(/^./, (x) => x.toUpperCase())}
            </li>
          ))}
        </ul>
      )}
      <footer className="mcard-foot">
        {mine ? (
          <span className="mcard-using">
            <Icon name="check" size={16} /> Used for new videos
          </span>
        ) : (
          <button data-action="models.default" type="button" className="btn btn-quiet btn-small" onClick={onChoose} disabled={busy}>
            {busy ? "Saving" : "Make it my default"}
          </button>
        )}
      </footer>
    </article>
  );
}

/** The narrator voices on offer (GET /api/voices), each with a sample to hear. */
function NarratorVoices() {
  const { data, error, loading } = useLoad<VoiceInfo[]>(() => api.voices(), []);
  const playing = useStore((s) => s.playing);
  const [sampling, setSampling] = useState<string | null>(null);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const play = async (v: VoiceInfo) => {
    const key = `nv-${v.voiceId}`;
    if (urls[v.voiceId]) return toggleAudio(key, urls[v.voiceId]);
    setSampling(v.voiceId);
    const r = await tapTool("voice_sample", { voice_id: v.voiceId, text: "Here's how I'd read your story. Every word, at the pace it deserves." }, `played a sample of ${v.name} on the Models panel`, { keepCards: true });
    setSampling(null);
    const url = r?.cards[0]?.url;
    if (url) {
      setUrls((u) => ({ ...u, [v.voiceId]: url }));
      toggleAudio(key, url);
    } else if (r) toast("No sample came back for that voice.", "bad");
  };
  return (
    <section className="voices-list" aria-labelledby="voices-h">
      <h2 id="voices-h" className="section-title">
        Narrator voices
      </h2>
      <p className="muted">Any of these can narrate, or voice a character. Your Director casts one for each character by how you describe them; you can always swap.</p>
      {error && <ErrorNote text={`Couldn't load the voices. ${error}`} />}
      {loading && !data && <div className="vrow-skel" aria-label="Loading the voices" />}
      {!!data?.length && (
        <ul className="vrows">
          {data.map((v) => {
            const on = playing === `nv-${v.voiceId}`;
            return (
              <li key={v.voiceId} className="vrow">
                <button type="button" className={`card-audio-btn${on ? " is-on" : ""}`} onClick={() => void play(v)} disabled={sampling === v.voiceId} aria-label={on ? `Stop ${v.name}` : `Hear ${v.name}`}>
                  <Icon name={on ? "pause" : sampling === v.voiceId ? "wave" : "play"} size={16} />
                </button>
                <span className="vrow-text">
                  <strong>{v.name}</strong>
                  <span className="muted">{v.description}</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
